import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, RefreshCw, Loader2, Pencil, Wand2,
  ExternalLink, Lock, Undo2, Play,
} from "lucide-react";
import {
  listReviewQueue,
  setStoryboardApproval,
  finishStoryboardAutomation,
  updateStoryboardMeta,
  generateStoryboardDescription,
  regenerateStoryboardThumbnail,
  generateSceneImage,
  startStoryboardScenePreview,
  getStoryboard,
  listAutomationQueue,
  cancelAutomationJob,
  resumeOrphanedAutomateRuns,
  deleteStoryboard,
} from "@/lib/api";
import AutomationProgressBanner from "@/components/AutomationProgressBanner";
import AllStoryboardsPanel from "@/components/AllStoryboardsPanel";
import { Trash2 } from "lucide-react";

/**
 * Review & Approve queue — Phase 3.
 *
 * After Automate runs, the resulting storyboard lands here. The user
 * scans for failed scene renders, fixes them with inline regen
 * buttons, edits the title/description/thumbnail if needed, and
 * clicks Approve to lock the story for the future daily YouTube
 * publisher. A `Send back to draft` button on approved cards undoes
 * the lock for further editing.
 *
 * Filter pills at top: Needs Fix · Pending Review · Approved · All.
 * Failed jobs sort to the very top so the user sees them first.
 */

const FILTERS = [
  { key: "needs_fix", label: "Needs Fix" },
  { key: "pending", label: "Pending Review" },
  { key: "approved", label: "Approved" },
  { key: "all", label: "All" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

// "Took 12m 40s" / "Took 2h 14m" — used on every completed-story
// card and the avg-time stat at the top. Built for SECONDS (not ms)
// because the backend already converts the started/completed
// timestamps into a clean float in `job_duration_sec`.
function formatDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  const total = Math.round(sec);
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

// A storyboard is "stills-only" when its post_format is `stills` —
// scenes are rendered as still images and stitched into a slideshow
// (Ken Burns or static). For those, `video_done` stays at 0 by design
// and MUST NOT be treated as a failure.
function isStillsOnly(row) {
  return (row?.post_format || "video").toLowerCase() === "stills";
}

function rowMatchesFilter(row, filter) {
  const status = row.approval_status || "draft";
  const job = row.job_status || "";
  const stillsOnly = isStillsOnly(row);
  const hasFailures =
    job === "failed" ||
    (row.image_done < row.total) ||
    // Stills-only channels never render per-scene videos, so 0/N
    // videos is the normal completion state — only count missing
    // videos as a failure for actual video-mode storyboards.
    (!stillsOnly && row.video_done < row.total);
  if (filter === "all") return true;
  if (filter === "needs_fix") return status === "draft" && hasFailures;
  if (filter === "pending") return status === "draft" && !hasFailures;
  if (filter === "approved") return status === "approved";
  return true;
}

export default function ReviewApprove() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("needs_fix");
  const [expandedId, setExpandedId] = useState(null);
  // Content Factory now hosts TWO panels behind a tab switcher:
  //   queue → the automation review flow (default, unchanged behaviour)
  //   all   → the full storyboard library (recovery path for boards
  //           that never ran through automation, e.g. Create Your Own
  //           Story). Persisted in localStorage so the user's last
  //           choice sticks across navigations.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem("ytm.review.active_tab");
      return saved === "all" ? "all" : "queue";
    } catch {
      return "queue";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("ytm.review.active_tab", activeTab);
    } catch {
      /* noop — private browsing / storage full */
    }
  }, [activeTab]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listReviewQueue();
      setRows(res?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load review queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live automation queue — poll every 2s while any job is running
  // so the user lands here right after clicking Automate and sees
  // per-scene progress in real-time. When a job completes, refresh
  // the review queue so its card slides into the list below.
  const [automationJobs, setAutomationJobs] = useState([]);
  const [dismissedJobIds, setDismissedJobIds] = useState(() => new Set());
  const toastedRef = useRef(new Set());

  const refreshAutomationQueue = useCallback(async () => {
    try {
      const res = await listAutomationQueue();
      setAutomationJobs(res?.jobs || []);
    } catch {
      // Silent — transient errors shouldn't blow away the banner.
    }
  }, []);

  useEffect(() => {
    refreshAutomationQueue();
    const id = setInterval(refreshAutomationQueue, 2000);
    return () => clearInterval(id);
  }, [refreshAutomationQueue]);

  // Toast + reload review list on job completion. Tracked in a ref
  // so we don't re-fire on every poll for the same job_id. Only
  // toasts for jobs we previously saw as running — auto-stale-
  // swept zombies that arrive already-failed shouldn't pop a
  // scary error toast (they're a self-heal, not a user-visible
  // failure).
  const seenRunningRef = useRef(new Set());
  useEffect(() => {
    for (const j of automationJobs) {
      if (j.status === "running" || j.status === "queued") {
        seenRunningRef.current.add(j.job_id);
      }
      if (j.status === "completed" && !toastedRef.current.has(`done:${j.job_id}`)) {
        toastedRef.current.add(`done:${j.job_id}`);
        if (seenRunningRef.current.has(j.job_id)) {
          toast.success(
            `"${j.video_name || "Story"}" is ready — ${j.scenes_done}/${j.total_scenes} scenes built`,
          );
        }
        // Pull the freshly-finished storyboard into the review list.
        load();
      } else if (j.status === "failed" && !toastedRef.current.has(`fail:${j.job_id}`)) {
        toastedRef.current.add(`fail:${j.job_id}`);
        if (seenRunningRef.current.has(j.job_id)) {
          toast.error(`Automation failed: ${j.error || "unknown error"}`);
        }
        load();
      } else if (j.status === "cancelled" && !toastedRef.current.has(`cancel:${j.job_id}`)) {
        toastedRef.current.add(`cancel:${j.job_id}`);
        // Cancelled is user-driven, the cancel handler already toasts.
        load();
      }
    }
  }, [automationJobs, load]);

  // Only surface jobs that are still actively working — once a job
  // hits `completed` / `failed` / `cancelled`, its storyboard has
  // already been pulled into the Review queue below (see the effect
  // above), so leaving the big green "// COMPLETE" banner up top is
  // just noise. Users can still see the finished story as a card in
  // the list. `dismissedJobIds` handles manual mid-run dismissals.
  const visibleJobs = automationJobs.filter((j) => (
    !dismissedJobIds.has(j.job_id)
    && (j.status === "queued" || j.status === "running")
  ));
  const runningCount = visibleJobs.filter((j) => j.status === "running").length;
  const queuedCount = visibleJobs.filter((j) => j.status === "queued").length;

  // Any storyboards actively being built by the automation worker
  // are surfaced in the top banner — showing the same row down in the
  // review list is duplicative and confusing (user sees a NEEDS FIX
  // pill on a story that hasn't finished rendering yet). Once the
  // job completes / fails / is cancelled the entry drops off
  // `visibleJobs` above, so the storyboard falls back into the list
  // automatically with its real final state.
  //
  // We match by BOTH storyboard_id and normalized title so we still
  // dedupe when a fresh Automate spawned a new storyboard while an
  // old failed row with the same title is lingering below. Title
  // match is normalized (lowercase, collapsed whitespace) so trivial
  // differences don't defeat the filter.
  const normTitle = (s) => (s || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  const inFlightStoryboardIds = useMemo(
    () => new Set(
      visibleJobs
        .map((j) => j.storyboard_id)
        .filter(Boolean),
    ),
    [visibleJobs],
  );
  const inFlightTitles = useMemo(
    () => new Set(
      visibleJobs
        .map((j) => normTitle(j.video_name))
        .filter(Boolean),
    ),
    [visibleJobs],
  );
  const rowIsInFlight = useCallback(
    (r) => (
      inFlightStoryboardIds.has(r.storyboard_id)
      || inFlightTitles.has(normTitle(r.video_name || r.title))
    ),
    [inFlightStoryboardIds, inFlightTitles],
  );

  const filtered = useMemo(
    () => rows
      .filter((r) => !rowIsInFlight(r))
      .filter((r) => rowMatchesFilter(r, filter)),
    [rows, filter, rowIsInFlight],
  );

  // Counts for the pill badges so the user always sees how much is
  // waiting in each bucket — surfaces forgotten failures even when
  // the user is browsing Approved. Exclude in-flight storyboards so
  // the badge numbers match what's actually visible below.
  const counts = useMemo(() => {
    const out = { all: 0, needs_fix: 0, pending: 0, approved: 0 };
    for (const r of rows) {
      if (rowIsInFlight(r)) continue;
      out.all++;
      for (const f of FILTERS) {
        if (f.key !== "all" && rowMatchesFilter(r, f.key)) out[f.key]++;
      }
    }
    return out;
  }, [rows, rowIsInFlight]);

  // Build-time stats across COMPLETED automations. Lets the user
  // spot when a typical run starts taking longer than usual (and
  // notice the average improving as we keep optimising the
  // pipeline). Min/max sit alongside the average so a single
  // 2-hour outlier doesn't silently inflate the perceived norm.
  // Stage averages (image vs video) + spend (USD) come from the
  // per-job telemetry the backend computes in `_summarize_stage_telemetry`.
  const buildTimeStats = useMemo(() => {
    const durations = rows
      .map((r) => r.job_duration_sec)
      .filter((d) => Number.isFinite(d) && d > 0);
    const imgAvgs = rows
      .map((r) => r.image_avg_sec)
      .filter((d) => Number.isFinite(d) && d > 0);
    const vidAvgs = rows
      .map((r) => r.video_avg_sec)
      .filter((d) => Number.isFinite(d) && d > 0);
    const totalImageCost = rows.reduce(
      (a, r) => a + (Number(r.image_cost_usd) || 0), 0,
    );
    const totalVideoCost = rows.reduce(
      (a, r) => a + (Number(r.video_cost_usd) || 0), 0,
    );
    const totalCost = totalImageCost + totalVideoCost;
    const avg = (xs) => xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
    return {
      count: durations.length,
      avg: avg(durations),
      fastest: durations.length ? Math.min(...durations) : 0,
      slowest: durations.length ? Math.max(...durations) : 0,
      imageAvg: avg(imgAvgs),
      videoAvg: avg(vidAvgs),
      totalImageCost,
      totalVideoCost,
      totalCost,
      avgStoryCost: rows.length ? totalCost / Math.max(1, rows.length) : 0,
    };
  }, [rows]);

  const handleApprovalChange = async (row, status) => {
    try {
      await setStoryboardApproval(row.storyboard_id, status);
      toast.success(
        status === "approved"
          ? "Approved · ready for daily publisher"
          : "Sent back to draft",
      );
      // Optimistic local update so the card flips immediately
      // without waiting for the re-fetch.
      setRows((prev) => prev.map((r) => (
        r.storyboard_id === row.storyboard_id
          ? { ...r, approval_status: status }
          : r
      )));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update approval");
    }
  };

  const handleFinishAutomation = async (row) => {
    try {
      const res = await finishStoryboardAutomation(row.storyboard_id);
      if (res?.status === "queued") {
        toast.success(`Queued · position ${res.queue_position} (${res.remaining_scenes} scenes to finish)`);
      } else {
        toast.success(`Resuming · ${res.remaining_scenes} scene${res.remaining_scenes === 1 ? "" : "s"} left to build`);
      }
      // Trigger an immediate queue refresh so the new banner shows
      // up without waiting for the 2s poll tick.
      refreshAutomationQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resume automation");
    }
  };

  const handleDelete = async (row) => {
    // Two-tap confirmation with a preview of what's going away — this
    // wipes the storyboard, every scene render, every version, and
    // any queued job. Once gone, only a Mongo restore can bring it
    // back, so make the user read the title before nuking.
    const title = row.video_name || row.title || "this story";
    if (!window.confirm(
      `Delete "${title}"?\n\nThis removes the storyboard, every scene render, every version, and any queued automation job. This can't be undone.`,
    )) return;
    // Optimistic remove — feels instant. Rollback on error.
    const before = rows;
    setRows((prev) => prev.filter((r) => r.storyboard_id !== row.storyboard_id));
    try {
      await deleteStoryboard(row.storyboard_id);
      toast.success(`Deleted "${title}"`);
      // Refresh in the background to keep totals + queue in sync.
      load();
    } catch (err) {
      setRows(before);
      toast.error(err?.response?.data?.detail || "Couldn't delete storyboard");
    }
  };

  return (
    <div className="space-y-6" data-testid="review-approve-page">
      <header className="space-y-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-yellow-400">
          // PHASE 3 · REVIEW & APPROVE
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Content Automation Factory
        </h1>
        <p className="text-zinc-300 max-w-3xl">
          Content being built for review and approval — or browse
          every storyboard you own across all channels.
        </p>
        {(buildTimeStats.count > 0 || buildTimeStats.totalCost > 0) && (
          <div
            data-testid="review-build-time-stats"
            className="mt-3 border-2 border-emerald-400/30 bg-emerald-400/5 rounded-sm px-3 py-2 space-y-1 font-mono text-[11px]"
          >
            {buildTimeStats.count > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 uppercase tracking-[0.2em]">
                <span className="text-emerald-300 font-bold">
                  {buildTimeStats.count} {buildTimeStats.count === 1 ? "story" : "stories"} timed
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-white">
                  avg <span className="text-emerald-200 font-bold">{formatDuration(buildTimeStats.avg)}</span>
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-300">
                  fastest <span className="text-white font-bold">{formatDuration(buildTimeStats.fastest)}</span>
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-300">
                  slowest <span className="text-white font-bold">{formatDuration(buildTimeStats.slowest)}</span>
                </span>
              </div>
            )}
            {(buildTimeStats.imageAvg > 0 || buildTimeStats.videoAvg > 0) && (
              <div
                data-testid="review-stage-avg-stats"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 normal-case tracking-normal text-zinc-300"
              >
                <span className="text-zinc-400 uppercase tracking-[0.18em] text-[10px]">per-scene avg</span>
                {buildTimeStats.imageAvg > 0 && (
                  <span>
                    image <span className="text-white font-bold">{formatDuration(buildTimeStats.imageAvg)}</span>
                  </span>
                )}
                <span className="text-zinc-500">·</span>
                {buildTimeStats.videoAvg > 0 && (
                  <span>
                    video <span className="text-white font-bold">{formatDuration(buildTimeStats.videoAvg)}</span>
                  </span>
                )}
              </div>
            )}
            {buildTimeStats.totalCost > 0 && (
              <div
                data-testid="review-cost-stats"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 normal-case tracking-normal text-zinc-300"
              >
                <span className="text-amber-300 uppercase tracking-[0.18em] text-[10px]">spend</span>
                <span>
                  total <span className="text-amber-200 font-bold">${buildTimeStats.totalCost.toFixed(2)}</span>
                </span>
                <span className="text-zinc-500">·</span>
                <span>
                  images <span className="text-white font-bold">${buildTimeStats.totalImageCost.toFixed(2)}</span>
                </span>
                <span className="text-zinc-500">·</span>
                <span>
                  videos <span className="text-white font-bold">${buildTimeStats.totalVideoCost.toFixed(2)}</span>
                </span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-400">
                  avg / story <span className="text-amber-200 font-bold">${buildTimeStats.avgStoryCost.toFixed(2)}</span>
                </span>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Two-panel switcher — see comment on `activeTab` state.
          Rendered as pill buttons to match the existing filter-pill
          aesthetic rather than shadcn tabs (which use a muted
          background that clashes with the terminal palette). */}
      <div
        role="tablist"
        aria-label="Content Factory sections"
        data-testid="review-tab-switcher"
        className="flex items-center gap-2 border-b border-zinc-800 pb-3"
      >
        {[
          { key: "queue", label: "Automation Queue", testid: "review-tab-queue" },
          { key: "all", label: "All Storyboards", testid: "review-tab-all" },
        ].map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={t.testid}
              data-active={active ? "true" : "false"}
              onClick={() => setActiveTab(t.key)}
              className={[
                "font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 rounded-sm border-2 transition-colors",
                active
                  ? "border-yellow-400 text-black bg-yellow-300"
                  : "border-zinc-700 text-zinc-200 hover:border-yellow-400/60 hover:text-yellow-200",
              ].join(" ")}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === "all" ? (
        <AllStoryboardsPanel />
      ) : (
        <>

      {visibleJobs.length > 0 && (
        <section
          data-testid="review-automation-queue"
          className="space-y-2 border-2 border-cyan-400/30 rounded-sm p-3 bg-cyan-400/5"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300">
              // AUTOMATION QUEUE · {runningCount} running · {queuedCount} queued
            </div>
            {visibleJobs.filter((j) => j.status === "completed" || j.status === "failed").length > 0 && (
              <button
                type="button"
                data-testid="review-automation-dismiss-all"
                onClick={() => {
                  setDismissedJobIds((prev) => {
                    const next = new Set(prev);
                    for (const j of visibleJobs) {
                      if (j.status === "completed" || j.status === "failed") {
                        next.add(j.job_id);
                      }
                    }
                    return next;
                  });
                }}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-zinc-100"
              >
                dismiss finished
              </button>
            )}
          </div>
          {visibleJobs.map((job) => (
            <AutomationProgressBanner
              key={job.job_id}
              job={job}
              onDismiss={() => setDismissedJobIds((prev) => {
                const next = new Set(prev); next.add(job.job_id); return next;
              })}
              onCancel={async () => {
                if (!window.confirm(`Cancel "${job.video_name || "this job"}"? Any partial renders already saved will stay on the storyboard.`)) return;
                try {
                  await cancelAutomationJob(job.job_id);
                  toast.success("Job cancelled");
                  refreshAutomationQueue();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't cancel job");
                }
              }}
              onResume={async () => {
                try {
                  const res = await resumeOrphanedAutomateRuns();
                  if (res?.resumed > 0) {
                    toast.success(`Re-launched ${res.resumed} stalled job${res.resumed > 1 ? "s" : ""} — activity should resume in a moment`);
                  } else {
                    toast.info("Nothing to resume — the worker may already be processing. Refreshing…");
                  }
                  refreshAutomationQueue();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't resume job");
                }
              }}
            />
          ))}
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="review-filter-pills">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = counts[f.key] ?? 0;
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`review-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={[
                "font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm border-2 transition-colors",
                active
                  ? "border-yellow-400 text-black bg-yellow-300"
                  : "border-zinc-700 text-zinc-200 hover:border-yellow-400/60 hover:text-yellow-200",
              ].join(" ")}
            >
              {f.label} <span className="opacity-70">· {n}</span>
            </button>
          );
        })}
        <div className="grow" />
        <button
          type="button"
          data-testid="review-refresh"
          onClick={load}
          className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 border-2 border-zinc-700 text-zinc-200 hover:border-cyan-400 hover:text-cyan-200 rounded-sm transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-3 h-3" />
          refresh
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-400 font-mono text-sm flex items-center gap-2" data-testid="review-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          loading queue…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} totalRows={rows.length} />
      ) : (
        <ul className="space-y-4">
          {filtered.map((row) => (
            <ReviewCard
              key={row.storyboard_id}
              row={row}
              expanded={expandedId === row.storyboard_id}
              onToggleExpand={() => setExpandedId(
                expandedId === row.storyboard_id ? null : row.storyboard_id,
              )}
              onApprove={() => handleApprovalChange(row, "approved")}
              onUnapprove={() => handleApprovalChange(row, "draft")}
              onFinishAutomation={() => handleFinishAutomation(row)}
              onDelete={() => handleDelete(row)}
              onRowChanged={load}
            />
          ))}
        </ul>
      )}
        </>
      )}
    </div>
  );
}

function EmptyState({ filter, totalRows }) {
  const messages = {
    needs_fix: totalRows === 0
      ? "No automations have finished yet. Kick off Automate from the Storyboard page to populate this queue."
      : "Nothing to fix — every finished automation rendered cleanly. Switch to Pending Review to approve them.",
    pending: "No stories waiting for review.",
    approved: "Nothing approved yet. Approve a story below to queue it for daily publishing.",
    all: "No automations have finished yet.",
  };
  return (
    <div
      data-testid="review-empty-state"
      className="border-2 border-dashed border-zinc-700 rounded-sm p-8 text-center"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
        // EMPTY
      </div>
      <p className="text-zinc-300">{messages[filter] || messages.all}</p>
      <Link
        to="/storyboards"
        className="inline-block mt-4 font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 rounded-sm transition-colors"
        data-testid="review-empty-go-to-storyboards"
      >
        Go to Storyboard →
      </Link>
    </div>
  );
}

function ReviewCard({ row, expanded, onToggleExpand, onApprove, onUnapprove, onFinishAutomation, onDelete, onRowChanged }) {
  const isApproved = row.approval_status === "approved";
  const isFailed = row.job_status === "failed";
  const stillsOnly = isStillsOnly(row);
  // Stills-only storyboards NEVER render per-scene videos — the
  // slideshow assembly builds the final video from the images. So
  // 0/N videos is the intended completion state for those boards.
  const missingImages = row.image_done < row.total;
  const missingVideos = !stillsOnly && row.video_done < row.total;
  const hasSceneFailures = missingImages || missingVideos;
  const allRendered =
    row.total > 0
    && row.image_done === row.total
    && (stillsOnly || row.video_done === row.total);
  // Only show "Finish Automation" when there's actually work left
  // AND the card isn't approved (approved cards are locked).
  const canFinishAutomation = !isApproved && hasSceneFailures && row.total > 0;
  const [finishing, setFinishing] = useState(false);
  const onFinishClick = async () => {
    setFinishing(true);
    try { await onFinishAutomation?.(); }
    finally { setFinishing(false); }
  };

  return (
    <li
      data-testid={`review-card-${row.storyboard_id}`}
      className={[
        "border-2 rounded-sm overflow-hidden",
        isApproved ? "border-emerald-400/60 bg-emerald-400/5"
        : isFailed ? "border-red-400/60 bg-red-400/5"
        : hasSceneFailures ? "border-amber-400/60 bg-amber-400/5"
        : "border-zinc-700 bg-zinc-950",
      ].join(" ")}
    >
      <div className="flex flex-col md:flex-row gap-4 p-4">
        {/* Thumbnail */}
        <div className="md:w-64 flex-shrink-0">
          {row.thumbnail_url ? (
            <img
              src={row.thumbnail_url}
              alt=""
              className="w-full aspect-video object-cover rounded-sm border border-zinc-800"
              data-testid={`review-card-thumb-${row.storyboard_id}`}
            />
          ) : (
            <div className="w-full aspect-video bg-zinc-900 border border-zinc-800 rounded-sm flex items-center justify-center text-zinc-600 font-mono text-[10px]">
              no thumbnail
            </div>
          )}
        </div>

        {/* Center column */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <StatusBadge row={row} />
            <span className="font-mono text-[10px] text-zinc-500">
              updated {timeAgo(row.updated_at)}
            </span>
            {row.source_channel?.name && (
              <span className="font-mono text-[10px] text-cyan-300">
                · {row.source_channel.name}
              </span>
            )}
          </div>
          <h3
            className="text-lg md:text-xl font-bold text-white leading-snug"
            data-testid={`review-card-title-${row.storyboard_id}`}
          >
            {row.video_name || "Untitled"}
          </h3>

          {/* Scene pip strip */}
          {row.total > 0 && (
            <ScenePipStrip row={row} />
          )}

          {/* Counts line */}
          <div className="font-mono text-[11px] text-zinc-300">
            <span className="text-white font-bold">{row.image_done}/{row.total}</span> images
            {!isStillsOnly(row) && (
              <> · <span className="text-white font-bold">{row.video_done}/{row.total}</span> videos</>
            )}
            {isStillsOnly(row) && (
              <span
                className="ml-2 text-zinc-500"
                title="This channel builds slideshow videos from the still images — no per-scene video renders required."
              >
                · stills-only (no per-scene video)
              </span>
            )}
            {Number.isFinite(row.job_duration_sec) && row.job_duration_sec > 0 && (
              <span
                className="ml-2 text-emerald-300"
                data-testid={`review-card-duration-${row.storyboard_id}`}
                title={`Took ${formatDuration(row.job_duration_sec)} from automate start to completion`}
              >
                · took <span className="font-bold">{formatDuration(row.job_duration_sec)}</span>
              </span>
            )}
            {(Number(row.total_cost_usd) > 0) && (
              <span
                className="ml-2 text-amber-300"
                data-testid={`review-card-cost-${row.storyboard_id}`}
                title={`Image renders $${Number(row.image_cost_usd || 0).toFixed(2)} + video renders $${Number(row.video_cost_usd || 0).toFixed(2)}`}
              >
                · <span className="font-bold">${Number(row.total_cost_usd).toFixed(2)}</span>
                <span className="text-amber-200/70 normal-case">
                  {" "}(img ${Number(row.image_cost_usd || 0).toFixed(2)} · vid ${Number(row.video_cost_usd || 0).toFixed(2)})
                </span>
              </span>
            )}
            {(row.image_avg_sec > 0 || row.video_avg_sec > 0) && (
              <span
                className="ml-2 text-zinc-400 normal-case"
                data-testid={`review-card-stage-avg-${row.storyboard_id}`}
                title="Average time per scene to generate the image vs. render the video"
              >
                · per-scene img {formatDuration(row.image_avg_sec)} / vid {formatDuration(row.video_avg_sec)}
              </span>
            )}
            {row.job_error && (
              <span className="text-red-300 ml-2">· {row.job_error}</span>
            )}
          </div>

          {row.seo_description ? (
            <p className="text-[12px] text-zinc-400 line-clamp-2">
              {row.seo_description}
            </p>
          ) : (
            <p className="text-[12px] text-amber-300/80 font-mono">
              ⚠ no YouTube description yet
            </p>
          )}
        </div>

        {/* Right action column */}
        <div className="flex flex-col gap-2 md:w-52">
          {isApproved ? (
            <button
              type="button"
              data-testid={`review-unapprove-${row.storyboard_id}`}
              onClick={onUnapprove}
              className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-zinc-600 text-zinc-200 hover:border-amber-400 hover:text-amber-200 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              <Undo2 className="w-3 h-3" />
              Send back to draft
            </button>
          ) : (
            <button
              type="button"
              data-testid={`review-approve-${row.storyboard_id}`}
              onClick={onApprove}
              disabled={!allRendered}
              title={!allRendered ? "Fix all scene renders before approving" : "Lock this story for the daily publisher"}
              className={[
                "font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 rounded-sm transition-colors flex items-center justify-center gap-2",
                allRendered
                  ? "border-emerald-400 text-emerald-100 hover:text-black hover:bg-emerald-300"
                  : "border-zinc-700 text-zinc-500 cursor-not-allowed",
              ].join(" ")}
            >
              <CheckCircle2 className="w-3 h-3" />
              Approve
            </button>
          )}

          {canFinishAutomation && (
            <button
              type="button"
              data-testid={`review-finish-automation-${row.storyboard_id}`}
              onClick={onFinishClick}
              disabled={finishing}
              title="Resume automation — only re-renders scenes that still need work"
              className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-yellow-400 text-yellow-100 hover:text-black hover:bg-yellow-300 disabled:opacity-60 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              {finishing
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Play className="w-3 h-3" />}
              Finish automation
            </button>
          )}

          <button
            type="button"
            data-testid={`review-expand-${row.storyboard_id}`}
            onClick={onToggleExpand}
            title="Quick-edit title, description and scene beats without leaving the queue"
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-zinc-800 text-zinc-500 hover:border-yellow-400 hover:text-yellow-200 rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            <Pencil className="w-3 h-3" />
            {expanded ? "Close quick edit" : "Quick edit"}
          </button>

          <Link
            to={`/storyboards/${row.storyboard_id}`}
            data-testid={`review-open-${row.storyboard_id}`}
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-zinc-700 text-zinc-200 hover:border-cyan-400 hover:text-cyan-200 rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-3 h-3" />
            Open storyboard
          </Link>

          {onDelete && (
            <button
              type="button"
              data-testid={`review-delete-${row.storyboard_id}`}
              onClick={onDelete}
              title="Permanently delete this storyboard, every scene render, and any queued job. Can't be undone."
              className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-zinc-800 text-zinc-500 hover:border-red-500 hover:text-red-300 hover:bg-red-500/10 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-3 h-3" />
              Delete
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <InlineEditPanel
          row={row}
          locked={isApproved}
          onChanged={onRowChanged}
        />
      )}
    </li>
  );
}

function StatusBadge({ row }) {
  const status = row.approval_status || "draft";
  if (status === "approved") {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-200 border border-emerald-400/40 px-2 py-0.5 rounded-sm">
        ✓ approved
      </span>
    );
  }
  if (row.job_status === "failed") {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-200 border border-red-400/40 px-2 py-0.5 rounded-sm">
        ✗ automation failed
      </span>
    );
  }
  const stillsOnly = isStillsOnly(row);
  const stillsIncomplete = stillsOnly ? row.image_done < row.total : false;
  const videoIncomplete = !stillsOnly && (row.image_done < row.total || row.video_done < row.total);
  if (stillsIncomplete || videoIncomplete) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-sm">
        ⚠ needs fix
      </span>
    );
  }
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-200 border border-cyan-400/40 px-2 py-0.5 rounded-sm">
      pending review
    </span>
  );
}

function ScenePipStrip({ row }) {
  const total = row.total || 0;
  const scenes = row.scenes || [];
  const stillsOnly = isStillsOnly(row);
  return (
    <div
      data-testid={`review-pip-strip-${row.storyboard_id}`}
      className="grid gap-1"
      style={{ gridTemplateColumns: `repeat(${Math.min(total, 16)}, minmax(0, 1fr))` }}
    >
      {scenes.map((s) => {
        // Stills-only storyboards succeed with image-only — treat
        // "image ✓" as complete without waiting on a video_ok that
        // will never come.
        const isDone = stillsOnly ? s.image_ok : (s.image_ok && s.video_ok);
        const isFailed = stillsOnly ? !s.image_ok : (!s.image_ok || !s.video_ok);
        return (
          <div
            key={`pip-${s.scene_id}`}
            title={
              stillsOnly
                ? `Scene ${s.scene_idx + 1}: ${s.image_ok ? "image ✓" : "image ✗"} · stills-only (no per-scene video)`
                : `Scene ${s.scene_idx + 1}: ${s.image_ok ? "image ✓" : "image ✗"} · ${s.video_ok ? "video ✓" : "video ✗"}`
            }
            className={[
              "h-5 rounded-sm border flex items-center justify-center text-[8px] font-mono",
              isDone ? "border-emerald-400 bg-emerald-400/20 text-emerald-200"
              : isFailed ? "border-amber-400 bg-amber-400/10 text-amber-200"
              : "border-zinc-700 bg-zinc-900 text-zinc-500",
            ].join(" ")}
          >
            {s.scene_idx + 1}
          </div>
        );
      })}
    </div>
  );
}

/** Inline editing — title, description, thumbnail regen + per-scene
 *  image/video regen buttons. Locked when approved (forces user to
 *  click "Send back to draft" first so they don't silently edit a
 *  story queued for publish). */
function InlineEditPanel({ row, locked, onChanged }) {
  const [title, setTitle] = useState(row.video_name || "");
  const [desc, setDesc] = useState(row.seo_description || "");
  const [savingMeta, setSavingMeta] = useState(false);
  const [regenThumb, setRegenThumb] = useState(false);
  const [regenDesc, setRegenDesc] = useState(false);
  const [sceneBusy, setSceneBusy] = useState({});  // {scene_id: 'image'|'video'}
  const [scenes, setScenes] = useState(row.scenes || []);

  const saveMeta = async () => {
    if (locked) return;
    setSavingMeta(true);
    try {
      await updateStoryboardMeta(row.storyboard_id, {
        videoName: title.trim() || undefined,
        seoDescription: desc,
      });
      toast.success("Saved · title + description");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSavingMeta(false);
    }
  };

  const regenerateDescription = async () => {
    if (locked) return;
    setRegenDesc(true);
    try {
      const res = await generateStoryboardDescription(row.storyboard_id);
      if (res?.description) setDesc(res.description);
      toast.success("New SEO description drafted");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate description");
    } finally {
      setRegenDesc(false);
    }
  };

  const regenerateThumbnail = async () => {
    if (locked) return;
    setRegenThumb(true);
    try {
      await regenerateStoryboardThumbnail(row.storyboard_id);
      toast.success("Thumbnail render queued · refresh the card in ~30s");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate thumbnail");
    } finally {
      setRegenThumb(false);
    }
  };

  const regenerateSceneImage = async (sceneId) => {
    if (locked) return;
    setSceneBusy((p) => ({ ...p, [sceneId]: "image" }));
    try {
      const res = await generateSceneImage(row.storyboard_id, sceneId, {
        provider: "openai-gpt-image",
      });
      if (res?.ok) {
        toast.success("Scene image re-rendered");
      } else {
        toast.error(res?.error || "Couldn't regenerate scene image");
      }
      await refreshSceneStatus();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate scene image");
    } finally {
      setSceneBusy((p) => {
        const n = { ...p }; delete n[sceneId]; return n;
      });
    }
  };

  const regenerateSceneVideo = async (sceneId) => {
    if (locked) return;
    setSceneBusy((p) => ({ ...p, [sceneId]: "video" }));
    try {
      const res = await startStoryboardScenePreview(row.storyboard_id, sceneId);
      if (res?.ok || res?.preview_status === "running") {
        toast.success("Seedance video render queued (~30–60s)");
      } else {
        toast.error(res?.error || "Couldn't start render");
      }
      await refreshSceneStatus();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't kick off video render");
    } finally {
      setSceneBusy((p) => {
        const n = { ...p }; delete n[sceneId]; return n;
      });
    }
  };

  const refreshSceneStatus = async () => {
    // Pull the full storyboard and map its scenes to the lightweight
    // pip shape so the user sees fresh image/video status without
    // navigating away.
    try {
      const sb = await getStoryboard(row.storyboard_id);
      const fresh = (sb?.scenes || []).map((s, idx) => ({
        scene_idx: idx,
        scene_id: s.scene_id,
        image_ok: !!(s.image_url || s.image_gridfs_id),
        video_ok:
          (s.preview_status || "").toLowerCase() === "ready" ||
          (s.versions || []).some((v) => (v.status || "").toLowerCase() === "ready"),
        preview_status: (s.preview_status || "pending").toLowerCase(),
      }));
      setScenes(fresh);
    } catch {
      // Non-fatal — outer load() will reconcile on the next refresh.
    }
  };

  return (
    <div
      data-testid={`review-inline-edit-${row.storyboard_id}`}
      className="border-t border-zinc-800 p-4 space-y-4 bg-black/40"
    >
      {locked && (
        <div className="flex items-center gap-2 text-amber-200 font-mono text-[11px]" data-testid={`review-locked-banner-${row.storyboard_id}`}>
          <Lock className="w-3 h-3" />
          locked · click <span className="text-amber-100">Send back to draft</span> to edit
        </div>
      )}

      {/* Meta editors */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1 block">
            // video title
          </label>
          <input
            data-testid={`review-edit-title-${row.storyboard_id}`}
            disabled={locked}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-zinc-950 border-2 border-zinc-700 rounded-sm px-3 py-2 text-white text-sm font-mono focus:border-yellow-400 outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            type="button"
            data-testid={`review-save-meta-${row.storyboard_id}`}
            disabled={locked || savingMeta}
            onClick={saveMeta}
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-emerald-400 text-emerald-100 hover:text-black hover:bg-emerald-300 disabled:opacity-50 rounded-sm transition-colors"
          >
            {savingMeta ? "saving…" : "save title + description"}
          </button>
          <button
            type="button"
            data-testid={`review-regen-thumb-${row.storyboard_id}`}
            disabled={locked || regenThumb}
            onClick={regenerateThumbnail}
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-3 py-2 border-2 border-cyan-400 text-cyan-100 hover:text-black hover:bg-cyan-300 disabled:opacity-50 rounded-sm transition-colors flex items-center gap-2"
          >
            {regenThumb ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
            re-render thumbnail
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
            // youtube description
          </label>
          <button
            type="button"
            data-testid={`review-regen-desc-${row.storyboard_id}`}
            disabled={locked || regenDesc}
            onClick={regenerateDescription}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-100 disabled:opacity-50"
          >
            {regenDesc ? "regenerating…" : "↻ regenerate via chatgpt"}
          </button>
        </div>
        <textarea
          data-testid={`review-edit-desc-${row.storyboard_id}`}
          disabled={locked}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          rows={6}
          className="w-full bg-zinc-950 border-2 border-zinc-700 rounded-sm px-3 py-2 text-white text-[12px] font-mono focus:border-yellow-400 outline-none disabled:opacity-50"
        />
      </div>

      {/* Per-scene action grid */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">
          // scenes ({scenes.length})
        </div>
        <div className="space-y-1.5">
          {scenes.map((s) => {
            const busy = sceneBusy[s.scene_id];
            const both = s.image_ok && s.video_ok;
            return (
              <div
                key={`scene-row-${s.scene_id}`}
                data-testid={`review-scene-row-${s.scene_id}`}
                className={[
                  "flex flex-wrap items-center gap-2 px-3 py-2 rounded-sm border",
                  both ? "border-emerald-400/40 bg-emerald-400/5"
                  : "border-amber-400/40 bg-amber-400/5",
                ].join(" ")}
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 w-14">
                  scene {s.scene_idx + 1}
                </span>
                <span className={[
                  "font-mono text-[10px]",
                  s.image_ok ? "text-emerald-300" : "text-amber-200",
                ].join(" ")}>
                  {s.image_ok ? "image ✓" : "image ✗"}
                </span>
                <span className={[
                  "font-mono text-[10px]",
                  s.video_ok ? "text-emerald-300" : "text-amber-200",
                ].join(" ")}>
                  {s.video_ok ? "video ✓" : "video ✗"}
                </span>
                <span className="grow" />
                <button
                  type="button"
                  data-testid={`review-regen-image-${s.scene_id}`}
                  disabled={locked || !!busy}
                  onClick={() => regenerateSceneImage(s.scene_id)}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border border-cyan-400/60 text-cyan-200 hover:text-black hover:bg-cyan-300 disabled:opacity-50 rounded-sm transition-colors flex items-center gap-1"
                >
                  {busy === "image" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  re-render image
                </button>
                <button
                  type="button"
                  data-testid={`review-regen-video-${s.scene_id}`}
                  disabled={locked || !!busy}
                  onClick={() => regenerateSceneVideo(s.scene_id)}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border border-yellow-400/60 text-yellow-200 hover:text-black hover:bg-yellow-300 disabled:opacity-50 rounded-sm transition-colors flex items-center gap-1"
                >
                  {busy === "video" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  re-render video
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
